if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Siblings of <media-gallery>, not descendants — see the comment
        // in main-product.liquid on why they live outside it.
        this.galleryNavButtons = this.parentElement
          ? Array.from(this.parentElement.querySelectorAll('[data-gallery-nav]'))
          : [];
        this.galleryNavButtons.forEach((button) => {
          button.addEventListener('click', this.goToAdjacentMedia.bind(this, button.dataset.galleryNav));
        });
        if (this.galleryNavButtons.length) {
          this.syncGalleryNavPosition = this.syncGalleryNavPosition.bind(this);
          window.addEventListener('resize', debounce(this.syncGalleryNavPosition, 200));
          window.requestAnimationFrame(this.syncGalleryNavPosition);
        }
      }

      goToAdjacentMedia(direction) {
        // Must be scoped to .product__media-item — a bare [data-media-id]
        // also matches the .product__media-toggle (zoom) button nested
        // inside every slide, which carries the RAW media id rather than
        // the slide's prefixed one. Stepping through that mixed list
        // alternated slide / button, and on a button setActiveMedia would
        // mark the button itself active while clearing is-active from
        // every real slide — leaving no slide visible at all (main image
        // vanished, thumbnails collapsed upward) until the next click
        // landed back on a slide.
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        const currentIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        if (currentIndex === -1) return;
        const step = direction === 'next' ? 1 : -1;
        const nextIndex = (currentIndex + step + mediaItems.length) % mediaItems.length;
        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
        window.requestAnimationFrame(this.syncGalleryNavPosition);
      }

      // The buttons live in .product__media-wrapper (so Dawn's own
      // overflow:hidden on media-gallery doesn't clip them), which also
      // contains the thumbnail row below the image — so a plain CSS
      // top:50% would center on the whole wrapper, landing too low.
      // Measuring the viewer's actual rect and positioning against that
      // directly keeps them centered on the image itself regardless of
      // thumbnail row height, image aspect ratio, or viewport size.
      syncGalleryNavPosition() {
        if (!this.galleryNavButtons.length || !this.parentElement) return;
        const viewerRect = this.elements.viewer.getBoundingClientRect();
        const wrapperRect = this.parentElement.getBoundingClientRect();
        const centerY = viewerRect.top - wrapperRect.top + viewerRect.height / 2;
        this.galleryNavButtons.forEach((button) => {
          const offset = button.dataset.galleryNav === 'next' ? 26 : -26;
          button.style.top = `${centerY + offset}px`;
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        // "thumbnail" layout keeps inactive items display:none, so
        // animations.js's reveal-on-scroll IntersectionObserver never sees
        // them until they become active — the first time any given item
        // switches to active, the observer treats that as its first-ever
        // reveal and fades it in from opacity:0.01 over ~750ms (only once
        // per item; already-revealed items switch back instantly). That
        // read as "image disappears, need to click again" since a second
        // click mid-fade jumps to the next image while the previous one's
        // fade was still resolving. Clearing the offscreen flag directly
        // makes it render at full opacity immediately regardless of
        // whether the observer has processed it yet.
        activeMedia?.classList?.remove('scroll-trigger--offscreen');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
